import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { API_ORIGIN } from '@/constants'
import { authorizedFetch } from '@/lib/api'

export type IngestSearchTerm = {
  id: string
  keyword: string
  country_code: string
  where: string
  enabled: boolean
  max_pages: number
  max_days_old: number
  last_sync_at?: string | null
  last_sync_status?: string
  last_sync_stats?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type IngestRunStatus = {
  run_id: string
  status: string
  stats?: Record<string, unknown>
  error?: string
}

export type LexiconSkillRef = {
  id: string
  name: string
  publication_status: string
}

export type LexiconDomainRef = {
  id: string
  label: string
  key: string
  publication_status: string
}

export type LexiconSelection = {
  id: string | null
  selected_skills: LexiconSkillRef[]
  selected_domains: LexiconDomainRef[]
  excluded_skills: LexiconSkillRef[]
  country_code: string
  where: string
  max_pages: number
  max_days_old: number
  last_sync_at?: string | null
  last_sync_status?: string
  last_sync_stats?: Record<string, unknown>
}

export type LexiconSelectionUpdate = {
  selected_skill_ids: string[]
  selected_domain_ids: string[]
  excluded_skill_ids: string[]
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json()
  if (!response.ok) {
    throw new Error((data as { error?: string }).error || response.statusText)
  }
  return data as T
}

export function useIngestSearchTerms() {
  return useQuery({
    queryKey: ['admin-ingest-terms'],
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/terms`)
      return parseJson<IngestSearchTerm[]>(response)
    },
  })
}

export function useLexiconSelection() {
  return useQuery({
    queryKey: ['admin-ingest-lexicon'],
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/lexicon`)
      return parseJson<LexiconSelection>(response)
    },
  })
}

export function useIngestMutations() {
  const queryClient = useQueryClient()

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-ingest-terms'] })
  const invalidateLexicon = () =>
    queryClient.invalidateQueries({ queryKey: ['admin-ingest-lexicon'] })

  const createTerm = useMutation({
    mutationFn: async (payload: Partial<IngestSearchTerm> & { keyword: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return parseJson<IngestSearchTerm>(response)
    },
    onSuccess: invalidate,
  })

  const updateTerm = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<IngestSearchTerm> & { id: string }) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/terms/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return parseJson<IngestSearchTerm>(response)
    },
    onSuccess: invalidate,
  })

  const deleteTerm = useMutation({
    mutationFn: async (id: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/terms/${id}`, {
        method: 'DELETE',
      })
      return parseJson<{ deleted: boolean }>(response)
    },
    onSuccess: invalidate,
  })

  const runSync = useMutation({
    mutationFn: async (termId?: string) => {
      const qs = termId ? `?term_id=${encodeURIComponent(termId)}` : ''
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/run${qs}`, {
        method: 'POST',
      })
      if (response.status === 403) {
        throw new Error('Marketplace ingest is not enabled for this tenant')
      }
      return parseJson<{ run_id: string; status: string }>(response)
    },
  })

  const pollRun = useMutation({
    mutationFn: async (runId: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/runs/${runId}`)
      return parseJson<IngestRunStatus>(response)
    },
  })

  const updateLexicon = useMutation({
    mutationFn: async (payload: LexiconSelectionUpdate) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/admin/opportunity-ingest/lexicon`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      return parseJson<LexiconSelection>(response)
    },
    onSuccess: invalidateLexicon,
  })

  return { createTerm, updateTerm, deleteTerm, runSync, pollRun, updateLexicon }
}
